import React, { useState, useEffect, FC } from 'react'
import { Row, Col, Table } from 'antd'
import { License, EndCustomer, RequestErrorObject, RequestErrorInterface, AvailableLicense } from '../../../types'
import { licensesService } from '../../../services'
import LicenseForm from './LicenseForm'
import { ResellerCard, handleError } from '../../../components'
import styles from './index.module.less'
import EditLicenseModal from './EditLicenseModal'

interface LicensesProps {
	isAdmin: boolean
	endCustomer: EndCustomer
}

interface LicenseState {
	loading: boolean
	licenses: License[]
}

interface AvailableLicenseState {
	loading: boolean
	licenses: AvailableLicense[]
}

const Licenses: FC<LicensesProps> = ({ endCustomer, isAdmin }) => {
	const [licenses, setLicenses] = useState<LicenseState>({ loading: false, licenses: [] })
	const [availableLicenses, setAvailableLicenses] = useState<AvailableLicenseState>({ loading: false, licenses: [] })

	const handleAvailableLicensesError = (error: RequestErrorInterface): RequestErrorObject => {
		const source = 'Available Licenses'
		const base = { status: error.response.status, source }

		switch (error.response.status) {
			case 500:
				return { ...base, description: 'Error getting available licenses, experienced server error.' }
			default:
				return { ...base, description: 'Error getting licenses.' }
		}
	}

	const handleGetLicensesError = (error: RequestErrorInterface): RequestErrorObject => {
		const source = 'Licenses'
		const base = { status: error.response.status, source }

		switch (error.response.status) {
			case 500:
				return { ...base, description: 'Error getting licenses, experienced server error.' }
			default:
				return { ...base, description: 'Error getting licenses.' }
		}
	}

	const updateLicenses = React.useCallback(async (id: number): Promise<void> => {
		try {
			setLicenses({ loading: true, licenses: [] })
			const { data } = await licensesService.get(id)
			setLicenses({ loading: false, licenses: data })
		} catch (error) {
			handleError(error, handleGetLicensesError)
			setLicenses({ licenses: [], loading: false })
		}
	}, [])

	const updateAvailableLicenses = React.useCallback(async (id: number): Promise<void> => {
		try {
			setAvailableLicenses(prev => ({ ...prev, loading: true }))
			const { data: licenses } = await licensesService.getAvailable(id)
			setAvailableLicenses({ loading: false, licenses })
		} catch (error) {
			handleError(error, handleAvailableLicensesError)
			setAvailableLicenses({ licenses: [], loading: false })
		}
	}, [])

	const onConsumersUpdated = (serial: string, quantity: number): void => {
		setLicenses(prev => {
			const index = prev.licenses.findIndex(license => license.serial === serial)
			const newLicenses = [...prev.licenses]
			newLicenses[index] = { ...newLicenses[index], consumed: quantity }
			return { ...prev, licenses: newLicenses }
		})
	}

	const onLicenseUpdated = (serial: string, license: License) => {
		setLicenses(prev => {
			const index = prev.licenses.findIndex(license => license.serial === serial)
			const newLicenses = [...prev.licenses]
			newLicenses[index] = { ...newLicenses[index], ...license }
			return { ...prev, licenses: newLicenses }
		})
	}

	const onLicenseAdded = (license: License): void => {
		setLicenses(prev => ({ ...prev, licenses: [...prev.licenses, license] }))
		updateAvailableLicenses(endCustomer.id)
	}

	useEffect(() => {
		updateAvailableLicenses(endCustomer.id)
		updateLicenses(endCustomer.id)
	}, [endCustomer.id, updateAvailableLicenses, updateLicenses])

	return (
		<>
			<Row gutter={48}>
				<Col xxl={18} xl={16} lg={14} md={24}>
					<Table rowKey='serial' dataSource={licenses.licenses} loading={licenses.loading}>
						<Table.Column title='License Type' dataIndex='display_name' />
						{isAdmin && <Table.Column title='Partner' dataIndex='partner_id' />}
						<Table.Column title='License Key' dataIndex='serial' />
						<Table.Column title='Consumed' dataIndex='consumed' />
						<Table.Column title='Unconsumed' render={license => <span>{license.quantity - license.consumed}</span>} />
						<Table.Column title='Allocated' dataIndex='quantity' />
						<Table.Column
							title='Actions'
							render={license => (
								<EditLicenseModal
									customerId={endCustomer.id}
									license={license}
									onLicenseUpdated={onLicenseUpdated}
									onConsumersUpdated={onConsumersUpdated}
									isAdmin={isAdmin}
								/>
							)}
						/>
					</Table>
					<span className={styles.licenseFormTitle}>Add new license key to {endCustomer.name}</span>
					<LicenseForm
						availableLicensesLoading={availableLicenses.loading}
						customerId={endCustomer.id}
						availableLicenses={availableLicenses.licenses}
						onLicenseAdded={onLicenseAdded}
					/>
				</Col>
				<Col xxl={6} xl={8} lg={10} md={24}>
					<Row>
						<ResellerCard id={endCustomer.reseller_id} />
					</Row>
				</Col>
			</Row>
		</>
	)
}

export default Licenses
